import formatCurrency from '../../../../../utils/formatCurrency';
import { TAIL_QUASI_RECEIVABLE_LABEL } from '../../../../../utils/order/orderAccountsReceivable';

export const ORDER_PRIMARY_PHASE_METRICS = {
	COUNT: 'count',
	TOTAL_PRICE: 'totalPrice',
	PROFIT_RATE: 'profitRate',
	RECEIVABLE: 'receivable',
	QUASI_RECEIVABLE: 'quasiReceivable',
	PENDING_PREPAYMENT: 'pendingPrepayment',
	PAID_PREPAYMENT: 'paidPrepayment',
};

export const ORDER_PRIMARY_PHASE_METRIC_OPTIONS = [
	{ value: ORDER_PRIMARY_PHASE_METRICS.COUNT, label: '单数' },
	{ value: ORDER_PRIMARY_PHASE_METRICS.RECEIVABLE, label: '应收账款' },
	{ value: ORDER_PRIMARY_PHASE_METRICS.QUASI_RECEIVABLE, label: TAIL_QUASI_RECEIVABLE_LABEL },
	{ value: ORDER_PRIMARY_PHASE_METRICS.PENDING_PREPAYMENT, label: '待收预付' },
	{ value: ORDER_PRIMARY_PHASE_METRICS.PAID_PREPAYMENT, label: '已收预付' },
];

const METRIC_LABEL_BY_VALUE = {
	[ORDER_PRIMARY_PHASE_METRICS.COUNT]: '单数',
	[ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE]: '合同金额',
	[ORDER_PRIMARY_PHASE_METRICS.PROFIT_RATE]: '利润率',
	[ORDER_PRIMARY_PHASE_METRICS.RECEIVABLE]: '应收账款',
	[ORDER_PRIMARY_PHASE_METRICS.QUASI_RECEIVABLE]: TAIL_QUASI_RECEIVABLE_LABEL,
	[ORDER_PRIMARY_PHASE_METRICS.PENDING_PREPAYMENT]: '待收预付',
	[ORDER_PRIMARY_PHASE_METRICS.PAID_PREPAYMENT]: '已收预付',
};

export const OVERVIEW_KPI_KEY_TO_METRIC = {
	count: ORDER_PRIMARY_PHASE_METRICS.COUNT,
	totalPrice: ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE,
	receivableTotal: ORDER_PRIMARY_PHASE_METRICS.RECEIVABLE,
	quasiReceivableTotal: ORDER_PRIMARY_PHASE_METRICS.QUASI_RECEIVABLE,
	pendingPrepaymentTotal: ORDER_PRIMARY_PHASE_METRICS.PENDING_PREPAYMENT,
	paidPrepaymentTotal: ORDER_PRIMARY_PHASE_METRICS.PAID_PREPAYMENT,
};

export const DEFAULT_ACTIVE_METRICS = [ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE, ORDER_PRIMARY_PHASE_METRICS.COUNT];

export const ORDER_PRIMARY_PHASE_METRIC_DISPLAY_ORDER = [
	ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE,
	ORDER_PRIMARY_PHASE_METRICS.COUNT,
	ORDER_PRIMARY_PHASE_METRICS.RECEIVABLE,
	ORDER_PRIMARY_PHASE_METRICS.QUASI_RECEIVABLE,
	ORDER_PRIMARY_PHASE_METRICS.PENDING_PREPAYMENT,
	ORDER_PRIMARY_PHASE_METRICS.PAID_PREPAYMENT,
];

export const sortActiveMetrics = (activeMetrics) => ORDER_PRIMARY_PHASE_METRIC_DISPLAY_ORDER.filter((metric) => activeMetrics.includes(metric));

export const isPrimaryPhaseMetricActive = (activeMetrics, metric) => activeMetrics.includes(metric);

export const togglePrimaryPhaseMetric = (activeMetrics, metric) => {
	let next;
	if (activeMetrics.includes(metric)) {
		if (activeMetrics.length <= 1) return activeMetrics;
		next = activeMetrics.filter((item) => item !== metric);
	} else {
		next = [...activeMetrics, metric];
	}
	return sortActiveMetrics(next);
};

export const resolveBarWidthMetric = (activeMetrics) => {
	const sorted = sortActiveMetrics(activeMetrics);
	if (sorted.includes(ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE)) {
		return ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE;
	}
	if (sorted.includes(ORDER_PRIMARY_PHASE_METRICS.COUNT)) {
		return ORDER_PRIMARY_PHASE_METRICS.COUNT;
	}
	return sorted[0] ?? ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE;
};

export const resolveChartPrimaryMetric = (activeMetrics) => sortActiveMetrics(activeMetrics)[0] ?? ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE;

const PHASE_FIELD_BY_METRIC = {
	[ORDER_PRIMARY_PHASE_METRICS.COUNT]: 'count',
	[ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE]: 'totalPrice',
	[ORDER_PRIMARY_PHASE_METRICS.RECEIVABLE]: 'receivableTotal',
	[ORDER_PRIMARY_PHASE_METRICS.QUASI_RECEIVABLE]: 'quasiReceivableTotal',
	[ORDER_PRIMARY_PHASE_METRICS.PENDING_PREPAYMENT]: 'pendingPrepaymentTotal',
	[ORDER_PRIMARY_PHASE_METRICS.PAID_PREPAYMENT]: 'paidPrepaymentTotal',
};

const OVERVIEW_FIELD_BY_METRIC = {
	[ORDER_PRIMARY_PHASE_METRICS.COUNT]: 'count',
	[ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE]: 'totalPrice',
	[ORDER_PRIMARY_PHASE_METRICS.RECEIVABLE]: 'receivableTotal',
	[ORDER_PRIMARY_PHASE_METRICS.QUASI_RECEIVABLE]: 'quasiReceivableTotal',
	[ORDER_PRIMARY_PHASE_METRICS.PENDING_PREPAYMENT]: 'pendingPrepaymentTotal',
	[ORDER_PRIMARY_PHASE_METRICS.PAID_PREPAYMENT]: 'paidPrepaymentTotal',
};

export const isPrimaryPhaseCurrencyMetric = (metric) =>
	metric !== ORDER_PRIMARY_PHASE_METRICS.COUNT && metric !== ORDER_PRIMARY_PHASE_METRICS.PROFIT_RATE;

export const isPrimaryPhasePercentMetric = (metric) => metric === ORDER_PRIMARY_PHASE_METRICS.PROFIT_RATE;

/** 组合利润率（百分数）：利润合计 / 合同金额合计；合同金额为 0 时记 0 */
export const calcPrimaryPhaseProfitRatePercent = (trueProfitTotal, totalPrice) => {
	const price = Number(totalPrice) || 0;
	const profit = Number(trueProfitTotal) || 0;
	return price > 0 ? (profit / price) * 100 : 0;
};

export const formatPrimaryPhasePercentValue = (value) => `${(Number(value) || 0).toFixed(1)}%`;

export const getPrimaryPhaseMetricLabel = (metric) => METRIC_LABEL_BY_VALUE[metric] ?? '单数';

export const getPrimaryPhaseRowMetricValue = (row, metric = ORDER_PRIMARY_PHASE_METRICS.COUNT) => {
	if (metric === ORDER_PRIMARY_PHASE_METRICS.PROFIT_RATE) {
		return calcPrimaryPhaseProfitRatePercent(row?.trueProfitTotal, row?.totalPrice);
	}
	const field = PHASE_FIELD_BY_METRIC[metric] ?? 'count';
	return Number(row?.[field]) || 0;
};

export const getPrimaryPhaseOverviewMetricValue = (overview, metric = ORDER_PRIMARY_PHASE_METRICS.COUNT) => {
	if (metric === ORDER_PRIMARY_PHASE_METRICS.PROFIT_RATE) {
		return calcPrimaryPhaseProfitRatePercent(overview?.trueProfitTotal, overview?.totalPrice);
	}
	const field = OVERVIEW_FIELD_BY_METRIC[metric] ?? 'count';
	return Number(overview?.[field]) || 0;
};

export const formatPrimaryPhaseMetricAxisValue = (value) => {
	const num = Number(value) || 0;
	if (Math.abs(num) >= 10000) return `${(num / 10000).toFixed(num >= 100000 ? 0 : 1)}万`;
	return String(num);
};

export const formatPipelinePhaseAmount = (value) => {
	const num = Number(value) || 0;
	if (num === 0) return '¥0';
	if (Math.abs(num) >= 10000) return `¥${formatPrimaryPhaseMetricAxisValue(num)}`;
	return formatCurrency(num);
};

export const formatPrimaryPhaseMetricValue = (value, metric = ORDER_PRIMARY_PHASE_METRICS.COUNT) => {
	if (isPrimaryPhasePercentMetric(metric)) return formatPrimaryPhasePercentValue(value);
	if (isPrimaryPhaseCurrencyMetric(metric)) return formatCurrency(value);
	return `${Number(value) || 0}`;
};

export const formatPrimaryPhaseFlowMetricLine = (phaseRow, metric) => {
	if (metric === ORDER_PRIMARY_PHASE_METRICS.COUNT) {
		return `${Number(phaseRow.count) || 0}单`;
	}
	const value = getPrimaryPhaseRowMetricValue(phaseRow, metric);
	return `${getPrimaryPhaseMetricLabel(metric)} ${formatPrimaryPhaseMetricValue(value, metric)}`;
};

export const buildPrimaryPhaseMetricTooltipLine = (row, metric) => {
	if (!row) return '';
	const count = Number(row.count) || 0;
	const receivable = Number(row.receivableTotal) || 0;
	const quasi = Number(row.quasiReceivableTotal) || 0;
	const prepay = Number(row.pendingPrepaymentTotal) || 0;
	const paidPrepay = Number(row.paidPrepaymentTotal) || 0;
	const primary = getPrimaryPhaseRowMetricValue(row, metric);
	const primaryLabel = getPrimaryPhaseMetricLabel(metric);

	if (metric === ORDER_PRIMARY_PHASE_METRICS.COUNT) {
		return `${primaryLabel} ${primary} 单<br/>合同 ${formatCurrency(row.totalPrice ?? 0)}<br/>应收账款 ${formatCurrency(receivable)}<br/>${TAIL_QUASI_RECEIVABLE_LABEL} ${formatCurrency(quasi)}<br/>待收预付 ${formatCurrency(prepay)}<br/>已收预付 ${formatCurrency(paidPrepay)}`;
	}

	return `${primaryLabel} ${formatCurrency(primary)}<br/>${count} 单 · 合同 ${formatCurrency(row.totalPrice ?? 0)}<br/>应收账款 ${formatCurrency(receivable)}<br/>${TAIL_QUASI_RECEIVABLE_LABEL} ${formatCurrency(quasi)}<br/>待收预付 ${formatCurrency(prepay)}<br/>已收预付 ${formatCurrency(paidPrepay)}`;
};
